// Card #3440 -- one scribe window per device (same origin).
//
// Two windows on one device ran two sync drainers and stranded marks via
// stale_write (HITS Del Mar, 2026-10-16). The drain lock itself is the
// hotfix's job; this is the human side: the NEWER window must not score or
// sync at all.
//
// Mechanism: the Web Locks API. The first window takes an exclusive
// "app-instance" lock and holds it until the page is gone. The browser
// releases a held lock when its page closes OR crashes, so a dead first
// window can never lock a scribe out: a later window's queued request is
// granted the moment it dies (no timers, no reload). No escape hatch by
// design (kiosk doctrine): there is nothing a scribe can tap to score in
// two windows.
//
// Fails OPEN when the API is missing (old browsers, insecure contexts):
// refusing to run would lock a scribe out of their only window, which is
// worse than the double-drain risk the sender lock already covers.

export const APP_INSTANCE_LOCK = "app-instance";

export const WINDOW_STATUS = Object.freeze({
  CHECKING: "checking",
  OWNER: "owner",
  BLOCKED: "blocked",
});

function getLocks() {
  try {
    return typeof navigator !== "undefined" &&
      navigator.locks &&
      typeof navigator.locks.request === "function"
      ? navigator.locks
      : null;
  } catch {
    return null;
  }
}

// Starts competing for the app-instance lock. `onStatus` is called with
// OWNER (we hold it) or BLOCKED (another window holds it; we stay queued and
// are promoted to OWNER the moment it lets go). Returns a disposer that
// releases the lock / abandons the queue.
export function acquireAppInstance(onStatus) {
  const locks = getLocks();
  if (!locks) {
    onStatus(WINDOW_STATUS.OWNER);
    return () => {};
  }
  const controller = new AbortController();
  let release = () => {};
  let disposed = false;

  const hold = (lock) => {
    if (!lock || disposed) return undefined;
    onStatus(WINDOW_STATUS.OWNER);
    return new Promise((resolve) => {
      release = resolve;
    });
  };

  // Absorbs the AbortError a disposed, still-queued request rejects with.
  const swallow = () => {};

  locks
    .request(APP_INSTANCE_LOCK, { mode: "exclusive", ifAvailable: true }, (lock) => {
      if (lock) return hold(lock);
      if (disposed) return undefined;
      onStatus(WINDOW_STATUS.BLOCKED);
      // Queue behind the holder: granted as soon as it is released.
      locks
        .request(APP_INSTANCE_LOCK, { mode: "exclusive", signal: controller.signal }, hold)
        .catch(swallow);
      return undefined;
    })
    .catch(() => {
      // A browser that exposes locks but refuses the request: fail open.
      if (!disposed) onStatus(WINDOW_STATUS.OWNER);
    });

  return () => {
    disposed = true;
    controller.abort();
    release();
  };
}
